import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Leaf, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = ({ onAddToCart }) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setFeaturedProducts(response.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchFeaturedProducts();
    const onFocus = () => fetchFeaturedProducts();
    window.addEventListener('focus', onFocus);
    const onVis = () => !document.hidden && fetchFeaturedProducts();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const handleAddToCart = (product) => {
    onAddToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div data-testid="home-page">
      <section data-testid="hero-section" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1750598243589-1cc3770356b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxnb2xkJTIwcGVyZnVtZSUyMGJvdHRsZSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MTQ5ODkwMnww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-primary mb-4">
              KRISHNA SUGANDH
            </h1>
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-8 font-body font-medium">
              Est. 2007 · Kannauj, India
            </p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-medium tracking-tight text-foreground mb-8">
              Where <span className="italic text-primary">Luxury</span> meets Fragrance
            </h2>
            <p className="text-lg md:text-xl font-light leading-relaxed text-foreground/90 mb-12 max-w-2xl mx-auto">
              Indulge in premium essential oils, traditional Indian attars, and exquisite sandalwood products crafted with centuries-old expertise.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/products"
                data-testid="hero-shop-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] flex items-center space-x-2"
              >
                <span>Shop Collection</span>
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
              <Link
                to="/wholesale"
                data-testid="hero-wholesale-button"
                className="border border-primary/30 text-primary hover:bg-primary/10 hover:border-primary rounded-full px-10 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300"
              >
                Wholesale Inquiry
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4 font-medium">Our Heritage</p>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-wide text-foreground mb-6">
            The Essence of <span className="italic">Tradition</span>
          </h2>
          <p className="text-lg font-light leading-relaxed text-muted-foreground max-w-3xl mx-auto">
            From the perfume capital of India, we bring you authentic fragrances crafted through time-honored methods passed down through generations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-card/30 backdrop-blur-md border border-white/5 p-8 hover:bg-card/50 transition-all duration-300"
          >
            <Sparkles className="text-primary mb-4" size={32} strokeWidth={1.5} />
            <h3 className="font-display text-2xl mb-3">Pure & Natural</h3>
            <p className="text-sm font-light leading-relaxed text-muted-foreground">
              100% organic ingredients sourced from nature, ensuring the highest quality and authenticity in every drop.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card/30 backdrop-blur-md border border-white/5 p-8 hover:bg-card/50 transition-all duration-300"
          >
            <Leaf className="text-primary mb-4" size={32} strokeWidth={1.5} />
            <h3 className="font-display text-2xl mb-3">Traditional Craft</h3>
            <p className="text-sm font-light leading-relaxed text-muted-foreground">
              Handcrafted using ancient Deg Bhapka distillation method, preserving the artistry of Kannauj's perfumery.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-card/30 backdrop-blur-md border border-white/5 p-8 hover:bg-card/50 transition-all duration-300"
          >
            <Award className="text-primary mb-4" size={32} strokeWidth={1.5} />
            <h3 className="font-display text-2xl mb-3">Since 2007</h3>
            <p className="text-sm font-light leading-relaxed text-muted-foreground">
              Nearly two decades of excellence in manufacturing and exporting premium fragrances worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {featuredProducts.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4 font-medium">Featured Products</p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-wide text-foreground mb-6">
              Signature <span className="italic">Collection</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="featured-products">
            {featuredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              data-testid="view-all-products-button"
              className="text-primary hover:text-primary/80 uppercase tracking-widest text-sm font-medium hover:underline underline-offset-8 decoration-1 inline-flex items-center space-x-2"
            >
              <span>View All Products</span>
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </section>
      )}

      <section className="py-24 md:py-32 px-6 md:px-12 bg-card/30">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_essence-store-24/artifacts/kk63o7fn_Copilot_20260422_151636.png"
              alt="Krishna Sugandh — rose harvesting in Kannauj and master perfumer crafting bespoke fragrances"
              className="w-full h-[500px] object-cover"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-primary font-medium">About Us</p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-wide text-foreground">
              Rooted in <span className="italic">Kannauj</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground">
              Krishna Sugandh, established in 2007, is a distinguished name in the world of natural fragrances. Based in Kannauj, on the sacred banks of the Ganges, we preserve centuries-old traditions of perfumery.
            </p>
            <p className="text-lg font-light leading-relaxed text-muted-foreground">
              Our commitment to quality and authenticity has made us trusted manufacturers, traders, and exporters of premium essential oils, attars, and sandalwood products to discerning customers worldwide.
            </p>
            <Link
              to="/about"
              data-testid="learn-more-button"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 uppercase tracking-widest text-sm font-medium hover:underline underline-offset-8 decoration-1"
            >
              <span>Learn More</span>
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};